import { Alert, AlertTitle, Button } from '@mui/material';
import backgroundDoodleOrnament from 'assets/background-doodle.png';
import { AxiosResponse } from 'axios';
import { AuthCenteredPanel } from 'components/auth/AuthCenteredPanel';
import AuthForm from 'components/auth/AuthForm/AuthForm';
import { Ornament } from 'components/Ornament';
import { getRoutePath } from 'config/routes';
import {
  AxiosErrorResponseHandler,
  AxiosErrorTypes,
  TranslatedError
} from 'features/authentication/AxiosErrorResponseHandler';
import { loginToBackend } from 'pages/login/LoginCallbackPage';
import { ReactElement, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import adcellClient from 'services/adcell';
import { authService, LoginTokens } from 'services/auth/AuthService';
import { authApiClient } from 'services/backofficeIntegration/http/client/createAuthApiClient';
import { useAppDispatch } from 'store/hooks';

interface LoginCodeRequest {
  code: string;
}

const errorTypes = [
  'INVALID_POST',
  'INVALID_TOKEN',
  'EMAIL_NOT_VERIFIED',
  'INVALID_USER',
  'USER_CREATE_FAILED',
  'INTERNAL_ERROR'
];

export const LoginCodePage = (): ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [error, setError] = useState<AxiosErrorTypes | null>(null);

  useEffect(() => {
    if (!location.hash.startsWith('#!')) {
      setError(new TranslatedError('login.error.missing_param'));
      return;
    }

    const locationHash = new URLSearchParams(location.hash.substring(2));
    const locationHashCode = locationHash.get('code');
    const locationHashError = locationHash.get('error');
    if (locationHashError != null && errorTypes.indexOf(locationHashError) != -1) {
      setError(new TranslatedError('login.error.' + locationHashError.toLowerCase()));
      return;
    }
    if (locationHashCode == null) {
      setError(new TranslatedError('login.error.missing_param'));
      return;
    }

    authApiClient
      .post<LoginCodeRequest, AxiosResponse<LoginTokens>>('/token', {
        code: locationHashCode
      })
      .then(response => {
        if (response && response.data) {
          authService.setTokens(response.data).then(() => {
            loginToBackend(
              {
                bid: adcellClient.getSavedBid(),
                login_type: response.data.login_type ?? 'google-one-tap'
              },
              null,
              dispatch,
              navigate
            ).catch(e => {
              setError(e);
            });
          });
        } else {
          setError(new TranslatedError('login.login_failed'));
        }
      })
      .catch(response => {
        setError(response);
      });
  }, [dispatch, navigate, location, setError]);

  const handleRedirectToLogin = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    navigate(getRoutePath('login'));
  };

  let alert;
  if (!error) {
    alert = (
      <Alert severity="info" variant="standardFullWidth">
        <AlertTitle>
          <FormattedMessage id="login.login_in_progress" />
        </AlertTitle>
      </Alert>
    );
  } else {
    alert = (
      <>
        <AxiosErrorResponseHandler error={error} />
        <Button color="primary" size="large" onClick={handleRedirectToLogin}>
          <FormattedMessage id="login.login_again" />
        </Button>
      </>
    );
  }

  return (
    <AuthCenteredPanel slogan="register.slogan">
      <AuthForm
        header="login.header"
        ornaments={<Ornament src={backgroundDoodleOrnament} bottom="0%" right="0%" />}
      >
        {alert}
      </AuthForm>
    </AuthCenteredPanel>
  );
};
